import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';

/** Format seconds as mm:ss */
function fmt(sec) {
  if (sec == null || isNaN(sec)) return '0:00';
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

const MIN_GAP = 1; // minimum 1s between IN and OUT

/**
 * VideoTrimmer - inline expandable trim UI for MP4 clips.
 *
 * Props:
 *   src           : string — video URL
 *   duration      : number — full clip length in seconds
 *   inPoint       : number — current IN in seconds
 *   outPoint      : number — current OUT in seconds
 *   onChange      : (nextIn, nextOut) — fires live as user drags
 *   onClose       : () => void — closes the expanded panel (optional)
 */
export default function VideoTrimmer({
  src,
  duration,
  inPoint,
  outPoint,
  onChange,
  onClose,
}) {
  const videoRef = useRef(null);
  const trackRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(inPoint ?? 0);
  const [dragging, setDragging] = useState(null); // 'in' | 'out' | 'scrub' | null

  const totalDur = Math.max(1, duration || 1);
  const safeIn = Math.max(0, Math.min(inPoint ?? 0, totalDur - MIN_GAP));
  const safeOut = Math.max(safeIn + MIN_GAP, Math.min(outPoint ?? totalDur, totalDur));

  // --- Playback: auto-stop at OUT point, seek to IN on play ---
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const onTime = () => {
      setCurrentTime(vid.currentTime);
      if (vid.currentTime >= safeOut) {
        vid.pause();
        setIsPlaying(false);
      }
    };
    const onPause = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);
    vid.addEventListener('timeupdate', onTime);
    vid.addEventListener('pause', onPause);
    vid.addEventListener('play', onPlay);
    return () => {
      vid.removeEventListener('timeupdate', onTime);
      vid.removeEventListener('pause', onPause);
      vid.removeEventListener('play', onPlay);
    };
  }, [safeOut]);

  const playTrim = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (isPlaying) {
      vid.pause();
      setIsPlaying(false);
      return;
    }
    // Start at IN (or wherever scrubber is, if inside trimmed range)
    if (vid.currentTime < safeIn || vid.currentTime >= safeOut) {
      vid.currentTime = safeIn;
    }
    vid.play().catch(() => {});
  };

  const resetTrim = () => {
    onChange(0, totalDur);
    const vid = videoRef.current;
    if (vid) {
      vid.pause();
      vid.currentTime = 0;
      setCurrentTime(0);
      setIsPlaying(false);
    }
  };

  // --- Drag handlers shared for IN / OUT / scrubber ---
  const posToTime = useCallback((clientX) => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return pct * totalDur;
  }, [totalDur]);

  useEffect(() => {
    if (!dragging) return;

    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const t = posToTime(clientX);
      if (dragging === 'in') {
        const clamped = Math.max(0, Math.min(t, safeOut - MIN_GAP));
        onChange(clamped, safeOut);
      } else if (dragging === 'out') {
        const clamped = Math.max(safeIn + MIN_GAP, Math.min(t, totalDur));
        onChange(safeIn, clamped);
      } else if (dragging === 'scrub') {
        const clamped = Math.max(safeIn, Math.min(t, safeOut));
        const vid = videoRef.current;
        if (vid) vid.currentTime = clamped;
        setCurrentTime(clamped);
      }
    };
    const onUp = () => setDragging(null);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging, safeIn, safeOut, totalDur, posToTime, onChange]);

  const pctIn = (safeIn / totalDur) * 100;
  const pctOut = (safeOut / totalDur) * 100;
  const pctCur = (Math.max(0, Math.min(currentTime, totalDur)) / totalDur) * 100;
  const trimDuration = safeOut - safeIn;

  const startDrag = (kind) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(kind);
  };

  return (
    <div className="mt-3 bg-zinc-900/60 border border-white/10 rounded-xl p-3" data-testid="video-trimmer">
      {/* Preview — ~300x170 */}
      <div className="mx-auto" style={{ maxWidth: 300 }}>
        <video
          ref={videoRef}
          src={src}
          className="w-full rounded-lg bg-black"
          style={{ aspectRatio: '16/9', maxHeight: 170 }}
          preload="metadata"
          playsInline
        />
      </div>

      {/* Readouts */}
      <div className="flex items-center justify-between mt-3 px-1 text-xs">
        <span className="text-zinc-400">
          In <span className="text-white font-mono ml-1">{fmt(safeIn)}</span>
        </span>
        <span className="text-[#b094f2] font-medium">
          Clip <span className="text-white font-mono ml-1">{fmt(trimDuration)}</span>
        </span>
        <span className="text-zinc-400">
          Out <span className="text-white font-mono ml-1">{fmt(safeOut)}</span>
        </span>
      </div>

      {/* Timeline */}
      <div className="px-3 py-6 relative">
        <div
          ref={trackRef}
          className="relative h-3 bg-zinc-800 rounded-full select-none touch-none"
          onMouseDown={(e) => {
            // Click outside handles on the track = move scrubber to that spot
            const t = posToTime(e.clientX);
            if (t >= safeIn && t <= safeOut) {
              const vid = videoRef.current;
              if (vid) vid.currentTime = t;
              setCurrentTime(t);
              setDragging('scrub');
            }
          }}
        >
          {/* Dimmed outside-trim regions */}
          <div
            className="absolute top-0 bottom-0 left-0 bg-black/60 rounded-l-full"
            style={{ width: `${pctIn}%` }}
          />
          <div
            className="absolute top-0 bottom-0 right-0 bg-black/60 rounded-r-full"
            style={{ width: `${100 - pctOut}%` }}
          />

          {/* Active trim region */}
          <div
            className="absolute top-0 bottom-0 bg-gradient-to-r from-[#915ee9]/50 to-[#7d46d9]/50"
            style={{ left: `${pctIn}%`, width: `${pctOut - pctIn}%` }}
          />

          {/* IN handle */}
          <button
            type="button"
            aria-label="Set in-point"
            data-testid="trim-in-handle"
            onMouseDown={startDrag('in')}
            onTouchStart={startDrag('in')}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-8 rounded-md bg-[#915ee9] border-2 border-white shadow-[0_2px_8px_rgba(145,94,233,0.6)] cursor-ew-resize flex items-center justify-center z-20"
            style={{ left: `${pctIn}%`, touchAction: 'none' }}
          >
            <span className="text-white text-[9px] font-bold">IN</span>
          </button>

          {/* OUT handle */}
          <button
            type="button"
            aria-label="Set out-point"
            data-testid="trim-out-handle"
            onMouseDown={startDrag('out')}
            onTouchStart={startDrag('out')}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-8 rounded-md bg-[#915ee9] border-2 border-white shadow-[0_2px_8px_rgba(145,94,233,0.6)] cursor-ew-resize flex items-center justify-center z-20"
            style={{ left: `${pctOut}%`, touchAction: 'none' }}
          >
            <span className="text-white text-[9px] font-bold">OUT</span>
          </button>

          {/* Playhead scrubber */}
          <div
            role="slider"
            aria-label="Playhead"
            onMouseDown={startDrag('scrub')}
            onTouchStart={startDrag('scrub')}
            className="absolute -top-1 w-4 h-5 -translate-x-1/2 cursor-pointer z-10 flex items-start justify-center"
            style={{ left: `${pctCur}%`, touchAction: 'none' }}
          >
            <div className="w-0.5 h-5 bg-white" />
            <div className="absolute -top-1.5 w-3 h-3 rounded-full bg-white shadow-md" />
          </div>
        </div>

        {/* Track end labels */}
        <div className="flex justify-between text-[10px] text-zinc-500 mt-2 px-1">
          <span>0:00</span>
          <span>{fmt(totalDur)}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-2 mt-1">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={playTrim}
            data-testid="trim-play-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#915ee9] hover:bg-[#a070ef] text-white text-xs font-medium transition-colors"
          >
            {isPlaying ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
            {isPlaying ? 'Pause' : 'Play trim'}
          </button>
          <button
            type="button"
            onClick={resetTrim}
            data-testid="trim-reset-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 text-xs font-medium transition-colors"
          >
            <RotateCcw className="h-3 w-3" />
            Reset
          </button>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            data-testid="trim-apply-btn"
            className="px-4 py-1.5 rounded-md bg-white/5 hover:bg-[#915ee9]/15 hover:border-[#915ee9]/40 border border-white/10 text-white text-xs font-medium transition-colors"
          >
            ✓ Apply
          </button>
        )}
      </div>
    </div>
  );
}
